import React, { memo, useCallback, useRef, useState } from "react";

export interface TemplateThumbProps {
  example?: { videoPath?: string; imagePaths?: string[] };
  aspect?: { width: number; height: number };
  isVideo?: boolean;
  coverTimestamp?: number;
  templateId: string;
  currentIndex: number;
  urlCache: Record<string, string>;
}

export const TemplateThumb = memo(
  ({
    example,
    aspect,
    isVideo,
    coverTimestamp,
    currentIndex,
    urlCache,
  }: TemplateThumbProps) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const primaryPath = isVideo
      ? example?.videoPath || ""
      : example?.imagePaths?.[currentIndex] || example?.imagePaths?.[0] || "";
    const url = urlCache[primaryPath] || "";
    const paddingTop =
      aspect?.width && aspect?.height
        ? `${(aspect.height / aspect.width) * 100}%`
        : "177.77%"; // default 9/16

    const handlePlay = useCallback(() => {
      if (isVideo && videoRef.current && !isPlaying) {
        setIsPlaying(true);
        videoRef.current.play().catch(() => {
          setIsPlaying(false);
        });
      }
    }, [isVideo, isPlaying]);

    const handlePause = useCallback(() => {
      if (isVideo && videoRef.current && isPlaying) {
        setIsPlaying(false);
        videoRef.current.pause();
        // Reset to cover timestamp
        if (coverTimestamp && videoRef.current.duration) {
          try {
            videoRef.current.currentTime = (coverTimestamp || 0) / 1000;
          } catch {}
        }
      }
    }, [isVideo, isPlaying, coverTimestamp]);

    const handlePlayButtonClick = useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        e.preventDefault();
        if (isPlaying) {
          handlePause();
        } else {
          handlePlay();
        }
      },
      [isPlaying, handlePlay, handlePause]
    );

    return (
      <div className="w-full relative rounded overflow-hidden border bg-muted group/video">
        <div style={{ paddingTop }} />
        <div className="absolute inset-0">
          {/* Render media only once the Firebase URL resolves — rendering
              `<img src="">` would show a broken-image icon briefly on some
              browsers. The outer `bg-muted` shows through in the meantime. */}
          {url &&
            (isVideo ? (
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                src={url}
                muted
                playsInline
                loop
                onLoadedMetadata={(e) => {
                  if (coverTimestamp && e.currentTarget.duration) {
                    try {
                      e.currentTarget.currentTime =
                        (coverTimestamp || 0) / 1000;
                    } catch {}
                  }
                }}
                controls={false}
              />
            ) : (
              <img
                className="w-full h-full object-cover"
                src={url}
                alt=""
              />
            ))}
        </div>
        {isVideo && (
          <>
            {/* Play/Pause button - centered, doesn't block card selection */}
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <button
                  type="button"
                  onClick={handlePlayButtonClick}
                  onTouchEnd={handlePlayButtonClick}
                  className="pointer-events-auto w-14 h-14 md:w-16 md:h-16 rounded-full bg-background/95 hover:bg-background flex items-center justify-center shadow-lg transition-transform motion-safe:hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <svg
                    className="w-7 h-7 md:w-8 md:h-8 text-foreground ml-1"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              </div>
            )}
            {/* Pause button — surfaces on hover during playback */}
            {isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 opacity-0 hover:opacity-100 transition-opacity">
                <button
                  type="button"
                  onClick={handlePlayButtonClick}
                  onTouchEnd={handlePlayButtonClick}
                  className="pointer-events-auto w-14 h-14 md:w-16 md:h-16 rounded-full bg-background/95 hover:bg-background flex items-center justify-center shadow-lg transition-transform motion-safe:hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <svg
                    className="w-7 h-7 md:w-8 md:h-8 text-foreground"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    // Return true if props are equal (skip re-render), false if different (re-render)

    // Different template entirely
    if (prevProps.templateId !== nextProps.templateId) {
      return false;
    }

    // Different carousel index
    if (prevProps.currentIndex !== nextProps.currentIndex) {
      return false;
    }

    // Type changed
    if (prevProps.isVideo !== nextProps.isVideo) {
      return false;
    }

    // Cover timestamp changed
    if (prevProps.coverTimestamp !== nextProps.coverTimestamp) {
      return false;
    }

    // Aspect ratio changed
    if (
      prevProps.aspect?.width !== nextProps.aspect?.width ||
      prevProps.aspect?.height !== nextProps.aspect?.height
    ) {
      return false;
    }

    // Example data changed (video path or image paths)
    if (prevProps.isVideo) {
      // For videos, compare video path
      if (prevProps.example?.videoPath !== nextProps.example?.videoPath) {
        return false;
      }
    } else {
      // For carousels, compare image paths array
      const prevPaths = prevProps.example?.imagePaths;
      const nextPaths = nextProps.example?.imagePaths;

      if (prevPaths?.length !== nextPaths?.length) {
        return false;
      }

      if (prevPaths && nextPaths) {
        for (let i = 0; i < prevPaths.length; i++) {
          if (prevPaths[i] !== nextPaths[i]) {
            return false;
          }
        }
      }
    }

    // Check if the actual URL for this specific template has changed
    const prevPath = prevProps.isVideo
      ? prevProps.example?.videoPath || ""
      : prevProps.example?.imagePaths?.[prevProps.currentIndex] ||
        prevProps.example?.imagePaths?.[0] ||
        "";
    const nextPath = nextProps.isVideo
      ? nextProps.example?.videoPath || ""
      : nextProps.example?.imagePaths?.[nextProps.currentIndex] ||
        nextProps.example?.imagePaths?.[0] ||
        "";

    // Path changed (shouldn't happen if above checks pass, but just in case)
    if (prevPath !== nextPath) {
      return false;
    }

    const prevUrl = prevProps.urlCache[prevPath];
    const nextUrl = nextProps.urlCache[nextPath];

    // URL is still loading or changed
    if (!prevUrl || !nextUrl || prevUrl !== nextUrl) {
      return false;
    }

    // Props are equal, skip re-render
    return true;
  }
);

TemplateThumb.displayName = "TemplateThumb";






